import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/utils';
import type {
  ModerationDashboardModuleDTO,
  ModerationFlaggedContentDTO,
  ModerationOffenderDTO,
} from '../dashboardTypes';
import { DashboardBusy, NotReadyBadge, moderationReadyState } from './DashboardNotReady';
import { formatModCount } from './dashboardChartPrimitives';

function severityTone(violations: number | undefined): string {
  const n = violations ?? 0;
  if (n >= 15) return 'text-rose-400';
  if (n >= 8) return 'text-amber-400';
  return 'text-yellow-300';
}

function formatWhen(value?: string): string {
  if (!value) return '—';
  try {
    return formatDistanceToNow(parseISO(value), { addSuffix: true });
  } catch {
    try {
      return format(parseISO(value), 'MMM d, HH:mm');
    } catch {
      return value;
    }
  }
}

/** Top Offending Users — Engagement-style ranking rail. */
export function TopOffendersCard({
  module,
  offenders,
  isLoading,
  className,
}: {
  module?: ModerationDashboardModuleDTO | undefined;
  offenders?: ModerationOffenderDTO[] | undefined;
  isLoading?: boolean;
  className?: string;
}) {
  const state = moderationReadyState(module?.availability);
  const title = module?.label ?? 'Top Offending Users';

  return (
    <section className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-rose-400">{title}</h2>
        <NotReadyBadge state={state} {...(module?.note ? { hint: module.note } : {})} />
      </div>
      {isLoading ? (
        <DashboardBusy className="h-32 rounded-xl border border-border/40 bg-[#141416]" />
      ) : state === 'not-ready' ? (
        <p className="rounded-xl border border-border/40 bg-[#141416] px-4 py-6 text-center text-xs text-muted-foreground">
          {module?.note ?? 'This module is not available yet.'}
        </p>
      ) : !offenders?.length ? (
        <p className="rounded-xl border border-border/40 bg-[#141416] px-4 py-6 text-center text-xs text-muted-foreground">
          No penalties issued in this window.
        </p>
      ) : (
        <ul className="space-y-2">
          {offenders.map((row) => {
            const name = row.displayName || row.username || `User ${row.userId}`;
            const inner = (
              <>
                {row.avatarUrl ? (
                  <img
                    src={row.avatarUrl}
                    alt=""
                    className="h-[3.25rem] w-[3.25rem] rounded-lg object-cover"
                  />
                ) : (
                  <span className="flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-lg bg-white/[0.06] text-sm font-semibold">
                    {name.charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0">
                  <p className="truncate text-sm font-semibold">{name}</p>
                  {row.username ? (
                    <p className="truncate text-[11px] text-muted-foreground">@{row.username}</p>
                  ) : null}
                </span>
                <span className="text-right text-sm font-bold tabular-nums">
                  {formatModCount(row.violations)}
                </span>
                <AlertTriangle
                  className={cn('size-4 shrink-0 justify-self-end', severityTone(row.violations))}
                  aria-label="Violation severity"
                />
              </>
            );
            const rowClass =
              'grid grid-cols-[3.25rem_minmax(0,1fr)_3.5rem_1.25rem] items-center gap-x-3 rounded-xl border border-border/40 bg-[#141416] px-2.5 py-2 transition-colors hover:border-rose-400/50';
            return (
              <li key={row.userId ?? row.username}>
                {row.userId != null ? (
                  <Link
                    to={ROUTES.userDetails(String(row.userId))}
                    className={rowClass}
                    aria-label={`${name}: open user`}
                  >
                    {inner}
                  </Link>
                ) : (
                  <div className={rowClass}>{inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Recent Content Flagged — Engagement-style ranking rail. */
export function RecentFlaggedCard({
  module,
  items,
  isLoading,
  className,
}: {
  module?: ModerationDashboardModuleDTO | undefined;
  items?: ModerationFlaggedContentDTO[] | undefined;
  isLoading?: boolean;
  className?: string;
}) {
  const state = moderationReadyState(module?.availability);
  const title = module?.label ?? 'Recent Content Flagged';

  return (
    <section className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-violet-400">{title}</h2>
        <NotReadyBadge state={state} {...(module?.note ? { hint: module.note } : {})} />
      </div>
      {isLoading ? (
        <DashboardBusy className="h-32 rounded-xl border border-border/40 bg-[#141416]" />
      ) : state === 'not-ready' ? (
        <p className="rounded-xl border border-border/40 bg-[#141416] px-4 py-6 text-center text-xs text-muted-foreground">
          {module?.note ?? 'This module is not available yet.'}
        </p>
      ) : !items?.length ? (
        <p className="rounded-xl border border-border/40 bg-[#141416] px-4 py-6 text-center text-xs text-muted-foreground">
          No flagged content in the pipeline right now.
        </p>
      ) : (
        <ul className="max-h-[28rem] space-y-2 overflow-y-auto">
          {items.map((item) => {
            const inner = (
              <>
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt=""
                    className="h-[3.25rem] w-[3.25rem] rounded-lg object-cover"
                  />
                ) : (
                  <span className="flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-lg bg-white/[0.06] text-[10px] text-muted-foreground">
                    {item.contentTypeLabel ?? 'Post'}
                  </span>
                )}
                <span className="min-w-0">
                  <p className="truncate text-sm font-semibold">
                    {item.riskCategory || 'Flagged content'}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {item.contentTypeLabel ?? item.contentType ?? 'Content'}
                    {' · '}
                    {item.creatorDisplayName || `Creator ${item.creatorUserId ?? '—'}`}
                  </p>
                </span>
                <span className="text-right text-[11px] text-muted-foreground">
                  {formatWhen(item.flaggedAt)}
                </span>
              </>
            );
            const rowClass =
              'grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-center gap-x-3 rounded-xl border border-border/40 bg-[#141416] px-2.5 py-2 text-inherit transition-colors hover:border-violet-400/50';
            return (
              <li key={item.caseId ?? item.postId}>
                {item.postId != null ? (
                  <Link
                    to={ROUTES.contentDetails(String(item.postId))}
                    className={rowClass}
                    aria-label={`Open content ${item.postId}`}
                  >
                    {inner}
                  </Link>
                ) : (
                  <div className={rowClass}>{inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
